import {
    AddRemoveParts,
    ActionBarFinalise,
    ArModal,
    ExploreLoading,
    FileDownloadModal,
    ModelErrorModal,
    ReturnToStoreBanner,
    SharingModal,
    SidePanelContainer,
    ThreeDScene,
    UnpublishedAlert,
} from '@dora/vivian';
import { useTranslations } from '@dora/vivian/dist/utils/TranslationsWrapper';
import { Close, Download } from '@mui/icons-material';
import { Box, Fab, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/system';
import { Effect } from 'effect';
import * as TO from 'fp-ts/TaskOption';
import * as Preact from 'preact';
import * as Hooks from 'preact/hooks';
import { AppControlContext } from '../contexts/appControl';
import { useSelectionsChangeMetrics } from '../hooks/analytics/useSelectionsChangeMetrics';
import { copyToClipboard, useSharing } from '../hooks/shared/useSharing';
import { useUndoRedo } from '../hooks/undo';
import { useAddRemoveParts } from '../hooks/useAddRemoveParts';
import * as ArState from '../hooks/useAr/arState';
import { useAr } from '../hooks/useAr/useAr';
import { useGenerateQrUrl } from '../hooks/useAr/useAr/useGenerateQrUrl';
import { getFurnitureArModel } from '../hooks/useAr/useCurrentConfigurationFurnitureArModel';
import { useExploreLoading } from '../hooks/useExploreLoading';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useGetBasket } from '../hooks/useGetBasket';
import { useGetBuildPropertyOptions } from '../hooks/useGetBuildPropertyOptions';
import { useGetParts } from '../hooks/useGetParts';
import { Cart, CheckoutLocation } from '../hooks/useParentApi';
import { useRenderer } from '../hooks/useRenderer';
import { useRenderableForCurrentConfiguration } from '../hooks/useRenderer/useRenderableForCurrentConfiguration';
import { useRenderableForCurrentInspirationProduct } from '../hooks/useRenderer/useRenderableForCurrentInspirationProduct';
import { useSelectProduct } from '../hooks/useSelectProduct';
import { useSelectHeroProp } from '../hooks/useSelectProduct/useSelectHeroProp';
import { useExploreTracking } from '../hooks/useTracking';
import * as Actions from '../store/actions/app';
import * as SkuDataCollectionActions from '../store/actions/app';
import * as Core from '../store/core';
import { useDispatch, useSelector } from '../store/redux';
import * as Selectors from '../store/selectors';
import { ComplexProduct } from '@fixtuur/backpack/src/fixtuur/configuration';
import { useCurrentConfiguration } from '../hooks/useRenderer/useRenderableForCurrentConfiguration/useDoraAssembleFurniture/useFurnitureChoices';
import { Fixtuur, Price } from '@fixtuur/backpack';
import { useInspirationProduct } from '../hooks/useInspirationProduct';

const Explore = (props: { addToBasket?: (cart: Cart, from: CheckoutLocation) => void }) => {
    const { t } = useTranslations();
    const dispatch = useDispatch();
    const appControl = Hooks.useContext(AppControlContext);
    const serviceConfig = useSelector(Selectors.Core.serviceConfig);

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const showConfigurePanelOnStart = useFeatureFlag('open-configure-on-start');
    const locale = useSelector(Selectors.Core.locale);
    const currency = useSelector(Selectors.Core.currency);

    const [onClose, setOnClose] = Hooks.useState<() => void>();
    const [undoRedoLoading, setUndoRedoLoading] = Hooks.useState(false);
    const [configurePanelOpen, setConfigurePanelOpen] = Hooks.useState(
        (showConfigurePanelOnStart && !isSmallScreen) ?? false,
    );

    const [selectionsChangeInfo, setSelectionsChangeInfo] = Hooks.useState<{
        isTracking: boolean | undefined;
        solutionMode: string | undefined;
        selectionType: string | undefined;
        isCompatible: boolean | undefined;
    }>({
        isTracking: undefined,
        solutionMode: undefined,
        selectionType: undefined,
        isCompatible: undefined,
    });

    const modelError = useSelector(Selectors.Core.modelError);
    const renderError = useSelector(Selectors.Core.renderError);

    const inConfiguration = useSelector(Selectors.Initialisation.inConfiguration);
    const environment = useSelector(Selectors.Core.environment);
    const shouldDisplayUnpublished = useSelector(Selectors.Initialisation.shouldDisplayUnpublished);

    const tenant = useSelector(Selectors.Core.tenant);

    const currentConfiguration = useCurrentConfiguration();
    const heroPart = Hooks.useMemo(
        () =>
            (currentConfiguration && Fixtuur.Configuration.heroParts(currentConfiguration)[0]) ??
            null,
        [currentConfiguration],
    );

    const { parts } = useGetParts(heroPart);
    const hasParts = parts.length > 0;

    const { inspirationProduct, inspirationProductGroup, inspirationProductId } =
        useInspirationProduct();

    const inspirationRenderable = useRenderableForCurrentInspirationProduct(inspirationProduct);
    const { furniture: configurationRenderable, isLoading: isConfigurationRenderableLoading } =
        useRenderableForCurrentConfiguration();

    const currentRenderable =
        inConfiguration && configurationRenderable && !isConfigurationRenderableLoading
            ? configurationRenderable
            : inspirationRenderable;

    const [showDimensions, setShowDimensions] = Hooks.useState(true);
    const [showFileDownloadModal, setShowFileDownloadModal] = Hooks.useState(false);

    const {
        onRendererReady,
        rendererOptions,
        modelGeneration,
        loading: rendererLoading,
        downloadConfigurationGlb,
    } = useRenderer(currentRenderable, showDimensions);

    useGetBasket(configurationRenderable, inspirationProduct);

    const price = useSelector(Selectors.Basket.price);
    const basket = useSelector(Selectors.Basket.basket);
    const removePricingEnabled = useFeatureFlag('remove-pricing-enabled');
    const disableProductCardPricing = useFeatureFlag('disable-product-card-pricing');

    const { undo, redo, undoCount, redoCount, update, showUndoRedo } = useUndoRedo();

    const { selectableBuildProperties, selectableBuildPropertiesLoading } =
        useGetBuildPropertyOptions(heroPart, inspirationProductGroup);

    const { proposeProductSelection, isProposalResolving } =
        useSelectProduct(selectableBuildProperties);

    const { proposeHeroSelection, isHeroSelectionResolving } = useSelectHeroProp(
        selectableBuildProperties,
        inspirationProductGroup,
    );

    const productCardsLoading = isProposalResolving || isHeroSelectionResolving;

    const { initialLoad, resetInitialLoad } = useExploreLoading(
        productCardsLoading || rendererLoading,
    );

    const { addRemoveCta, showAddParts } = useAddRemoveParts(resetInitialLoad, inspirationProduct);

    const addRemoveContent = addRemoveCta && (
        <AddRemoveParts.Component
            onClick={() => {
                closeArModal();
                addRemoveCta.onClick();
            }}
            status={addRemoveCta.status}
            addPartsMessage={t('configure.cta.body')}
            addPartsText={t('configure.cta.title')}
            buttonText={
                addRemoveCta.status === 'add' ? t('configure.cta.add') : t('configure.cta.remove')
            }
        />
    );

    const [selectedOptionId, setSelectedOptionId] = Hooks.useState<string | null>(null);
    Hooks.useEffect(() => {
        dispatch(Actions.selectPartToConfigureAction({ part: selectedOptionId }));
    }, [selectedOptionId]);

    const logoSrc = useSelector(Selectors.Core.logoSrc);

    const conversionId = useSelector(Selectors.ConversionId.conversionId);

    const addToBasket = props.addToBasket;

    const checkout =
        !removePricingEnabled && addToBasket && conversionId && basket
            ? (from: CheckoutLocation) => {
                  addToBasket(
                      {
                          conversionId,
                          price: basket.price,
                          products: basket.products.map(
                              ({
                                  productId,
                                  quantity,
                                  options,
                              }: {
                                  productId: string;
                                  quantity: number;
                                  options?: ComplexProduct.ComplexProduct | undefined;
                              }) => {
                                  const product: {
                                      id: string;
                                      quantity: number;
                                      options?: ComplexProduct.ComplexProduct;
                                  } = {
                                      id: productId,
                                      quantity,
                                  };
                                  if (options !== undefined || !options) {
                                      product.options = options;
                                  }
                                  return product;
                              },
                          ),
                      },
                      from,
                  );
                  dispatch(SkuDataCollectionActions.addToBasketAction());
                  appControl?.closeApp();
              }
            : undefined;

    const {
        sharingUrl,
        showAlert,
        setShowAlert,
        showSharingModal,
        closeSharingModal,
        openSharingModal,
    } = useSharing(inspirationProductId ?? null);

    const generateQrUrl = useGenerateQrUrl(inspirationProductId ?? null);

    const getModelUrl = Hooks.useMemo(
        () =>
            !serviceConfig
                ? TO.none
                : inConfiguration
                  ? getFurnitureArModel(
                        serviceConfig,
                        modelGeneration.gltfBlob,
                        modelGeneration.modelJson,
                    )
                  : TO.fromNullable(inspirationRenderable?.modelUrl),
        [inConfiguration, modelGeneration],
    );

    const usdzModelUrl = Hooks.useMemo(
        () => (inConfiguration ? undefined : inspirationRenderable?.usdzModelUrl),
        [inConfiguration, inspirationRenderable],
    );

    const arPlacement = inspirationRenderable?.placement === 'Wall' ? 'wall' : 'floor';

    const unpublishedProducts =
        basket?.products.filter(p => !p.published).map(p => p.productId) || [];

    const { closeArModal, openArModal, arState } = useAr({
        onCheckout: checkout ? () => checkout('ar') : undefined,
        delayLoad: true,
        getModelUrl,
        usdzModelUrl,
        placement: arPlacement,
        isAppLaunching: initialLoad,
        generateQrUrl,
    });

    const closeModals = () => {
        closeSharingModal();
        closeArModal();
    };

    // Close modals when app state changes. Allows us to generate a link that accurately reflects Inspiration vs Configuration and part selections.
    Hooks.useEffect(() => {
        closeModals();
    }, [
        addRemoveCta,
        isProposalResolving,
        isHeroSelectionResolving,
        showDimensions,
        selectedOptionId,
    ]);

    Hooks.useEffect(() => {
        if (
            (undoCount > 0 || redoCount > 0) &&
            !productCardsLoading &&
            !isHeroSelectionResolving &&
            !isProposalResolving &&
            !selectableBuildPropertiesLoading &&
            !rendererLoading
        ) {
            setUndoRedoLoading(false);
        }
    }, [productCardsLoading, selectableBuildProperties, rendererLoading]);

    useExploreTracking({
        partsLoading: productCardsLoading,
        hasParts,
        addRemoveCta,
        inConfiguration,
        showAddParts,
        initialLoad,
        heroProperties: selectableBuildProperties,
    });

    useSelectionsChangeMetrics(selectionsChangeInfo, setSelectionsChangeInfo);

    // clear thread scene state on close
    Hooks.useEffect(() => {
        if (appControl && appControl.enhanceCloseApp) {
            appControl?.enhanceCloseApp('clear_component_state', () => {
                resetInitialLoad();
                onClose && onClose();
            });
        }
    }, [appControl, onClose]);

    Hooks.useEffect(() => {
        if (!rendererLoading && selectionsChangeInfo?.isTracking === true) {
            setSelectionsChangeInfo({
                ...selectionsChangeInfo,
                isTracking: false,
            });
        }
    }, [rendererLoading]);

    Hooks.useEffect(() => {
        // Reset UI state on close
        setOnClose(() => () => {
            setConfigurePanelOpen(false);
            setSelectedOptionId(null);
        });
    }, []);

    const proposalLoading = inConfiguration
        ? !(configurationRenderable && !isConfigurationRenderableLoading) || productCardsLoading
        : initialLoad || productCardsLoading;

    const drawerContent = (
        <SidePanelContainer.Component
            trackMoreInfo={(price: string) => dispatch(Actions.moreInfoClickedAction(price))}
            trackVariantCategory={(category: string) =>
                dispatch(Actions.variantQuestionViewAction(category))
            }
            parts={parts}
            formatCurrency={(price: number) => Price.toLocaleString(price, locale, currency)}
            configurePanelConfirmationText={t('configure.panel.confirmation')}
            configurePanelTitle={t('configure.panel.title')}
            extraConfigureContent={addRemoveContent}
            onCloseClick={() => {
                setConfigurePanelOpen(false);
                dispatch(Actions.configurePanelToggleAction(false));
                closeModals();
            }}
            disableClose={renderError && !inConfiguration}
            setSelectedOptionId={setSelectedOptionId}
            selectedOptionId={selectedOptionId}
            onProductCardClick={(
                productId: string | null,
                partId: string,
                price: string | null,
                isCompatible: boolean,
            ) => {
                setUndoRedoLoading(true);
                update();
                proposeProductSelection(productId, partId, price);
                setSelectionsChangeInfo({
                    isTracking: true,
                    solutionMode: inConfiguration ? 'Configuration' : 'Variants',
                    selectionType: 'Part',
                    isCompatible: isCompatible,
                });
            }}
            onHeroPartOptionClick={(value, property, isCompatible) => {
                update();
                proposeHeroSelection(property, value);
                setSelectionsChangeInfo({
                    isTracking: true,
                    solutionMode: inConfiguration ? 'Configuration' : 'Variants',
                    selectionType: 'Property',
                    isCompatible: isCompatible,
                });
                setUndoRedoLoading(true);
            }}
            hidePrice={disableProductCardPricing}
            heroProperties={selectableBuildProperties}
            trackVariantSelection={(val: {
                value?: string | number | null;
                colourValue?: string | number | null;
            }) => {
                const { value, colourValue } = val;
                if (typeof colourValue === 'string') {
                    dispatch(Actions.variantColourSelectionAppliedAction(colourValue));
                } else if (value) dispatch(Actions.variantSelectionAppliedAction(value));
            }}
            trackCompatibility={(compatibility: string) =>
                dispatch(Actions.compatibilityToggledAction(compatibility))
            }
            reorderDimensions={tenant === 'traderadiators'}
        />
    );

    const showSceneConfigure =
        addRemoveCta !== undefined || hasParts || selectableBuildProperties.length > 0;

    return (
        <Box
            display="flex"
            height="100%"
            borderRadius={{ xs: '0px', md: '24px' }}
            overflow={{ md: 'hidden' }}
            width="100%"
            data-testid="explore-component"
            position="relative"
        >
            <ExploreLoading.Component
                show={initialLoad && !configurationRenderable}
                logoSrc={logoSrc}
                loadingText={t('loading.explorer.primary')}
                secondaryLoadingText={t('loading.explorer.secondary')}
            />
            <ActionBarFinalise.Component
                onAr={() => {
                    closeSharingModal();
                    openArModal();
                }}
                arText={t('ar.cta')}
                onCheckout={checkout ? () => checkout('explore') : undefined}
                basketPrice={price}
                hideBasketPrice={removePricingEnabled}
                onShare={() => {
                    closeArModal();
                    openSharingModal();
                    dispatch(Actions.shareModalOpenedAction());
                }}
                disableShare={productCardsLoading || isProposalResolving}
                sx={{
                    top: '20px',
                    right: '20px',
                }}
            />
            {inConfiguration && environment === 'development' && (
                <Fab
                    onClick={() => setShowFileDownloadModal(true)}
                    color="primary"
                    sx={{
                        position: 'absolute',
                        zIndex: 1000,
                        bottom: '20px',
                        right: '40%',
                    }}
                >
                    <Download />
                </Fab>
            )}
            <FileDownloadModal.Component
                open={showFileDownloadModal}
                onDownload={(fileName?: string) => {
                    Effect.runPromise(downloadConfigurationGlb(fileName, 'mixer'));
                    setShowFileDownloadModal(false);
                }}
                onCloseClick={() => setShowFileDownloadModal(false)}
            />
            <SharingModal.Component
                open={showSharingModal}
                onCloseClick={closeSharingModal}
                title={t('share.header')}
                url={sharingUrl()}
                onShareButtonClick={(target: 'email' | 'whatsapp' | 'facebook') =>
                    dispatch(Actions.shareButtonClickedAction(target))
                }
                onLinkClick={() => {
                    const url = sharingUrl();
                    if (url) {
                        copyToClipboard(url);
                        setShowAlert(true);
                        dispatch(Actions.shareLinkCopiedAction());
                        setTimeout(() => setShowAlert(false), 5000);
                    } else {
                        // TODO: error alert?
                        dispatch(
                            Core.error({
                                message: 'Failed to generate sharing URL for 3DX',
                                context: {
                                    type: 'log',
                                    location: 'Sharing Modal: onLinkClick',
                                },
                            }),
                        );
                    }
                }}
                facebookText={t('share.facebook')}
                whatsappText={t('share.whatsapp')}
                linkText={t('share.link')}
                emailText={t('share.email')}
                emailSubject={t('share.email_subject')}
                emailContent={t('share.email_content')}
                showLinkAlert={showAlert}
                linkAlertText={t('share.link_copy')}
                sx={{
                    right: '20px',
                    top: '88px',
                }}
            />
            <ArModal.Component
                // TODO: provide helpers for checking these things
                open={ArState.isOpen(arState)}
                title={t('qr.header')}
                onCloseClick={closeArModal}
                sx={{
                    right: '20px',
                    top: '88px',
                }}
                {...ArState.toArModalProps(arState)}
            />
            <ThreeDScene.Component
                renderError={renderError}
                inConfiguration={inConfiguration}
                onConfigurePanelToggle={open => {
                    setConfigurePanelOpen(open);
                    dispatch(Actions.configurePanelToggleAction(open));
                    closeModals();
                }}
                configurePanelOpen={configurePanelOpen}
                configPanelButtonText={t('configure.panel.button')}
                proposalLoading={proposalLoading}
                sceneLoading={
                    inConfiguration
                        ? !(configurationRenderable && !isConfigurationRenderableLoading)
                        : rendererLoading
                }
                showSceneConfigure={showSceneConfigure}
                drawerContent={drawerContent}
                showUndoRedo={showUndoRedo}
                canUndo={showUndoRedo && undoCount > 0 && !undoRedoLoading}
                canRedo={showUndoRedo && redoCount > 0 && !undoRedoLoading}
                onUndoClick={() => {
                    setUndoRedoLoading(true);
                    undo();
                }}
                onRedoClick={() => {
                    setUndoRedoLoading(true);
                    redo();
                }}
                onRendererReady={onRendererReady}
                rendererOptions={rendererOptions}
                onDimensionsToggle={() => {
                    dispatch(
                        Actions.dimensionsToggledAction({
                            isVisible: showDimensions,
                        }),
                    );
                    setShowDimensions(!showDimensions);
                }}
                showDimensions={showDimensions}
            />
            <ModelErrorModal.Component
                open={modelError}
                closeText={t('error.model.close')}
                onCloseClick={() => appControl?.closeApp()}
            />
            {shouldDisplayUnpublished && unpublishedProducts && unpublishedProducts.length > 0 && (
                <UnpublishedAlert.Component unpublishedProducts={unpublishedProducts} />
            )}
        </Box>
    );
};

export const Component = (props: {
    addToBasket?: (cart: Cart, from: CheckoutLocation) => void;
}) => {
    const theme = useTheme();
    const { t } = useTranslations();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const appControl = Hooks.useContext(AppControlContext);

    const closeApp = appControl?.closeApp ?? (() => {});

    return (
        <Box
            width="100%"
            height="100%"
            display="flex"
            justifyContent="start"
            overflow="hidden"
            flexDirection={isSmallScreen ? 'column' : 'row'}
        >
            {isSmallScreen && (
                <ReturnToStoreBanner.Component
                    onReturnToStoreClick={closeApp}
                    returnToStoreText={t('nav.return')}
                />
            )}

            <Explore addToBasket={props.addToBasket} />

            {!isSmallScreen && (
                <Fab
                    size="medium"
                    sx={{
                        backgroundColor: '#f5f5f5',
                        marginLeft: '24px',
                        flex: 'none',
                    }}
                    onClick={closeApp}
                >
                    <Close color="secondary" />
                </Fab>
            )}
        </Box>
    );
};
